import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Carousel } from 'foundations-components/transitional/components/Carousel';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { Image } from 'foundations-components/transitional/components/Image';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Fullscreen } from './Fullscreen';
import { VideoPlayer } from './VideoPlayer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NoopOverhang = () => null;
const CAROUSEL_MEDIA_HEIGHT = '360px';
export function MediaCarousel({
  items,
  onItemClick,
  onTrackInteraction,
  onFullscreenChange
}) {
  const [fullscreen, setFullscreen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const prevStepIndex = usePrevious(stepIndex);
  const imageElementRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({});
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [tallImagesWithBounce, setTallImagesWithBounce] = useState(new Set());

  // Define what constitutes a "tall" aspect ratio (very tall/narrow)
  const TALL_ASPECT_RATIO_THRESHOLD = 2; // height/width ratio > 2 is considered tall

  const triggerScrollBounce = useCallback(scrollContainer => {
    if (!scrollContainer) return;
    const originalScrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;

    // Only bounce if there's actually scrollable content
    if (scrollHeight <= clientHeight) return;

    // Scroll down a bit, then back up, then to original position
    const bounceDistance = Math.min(50, scrollHeight - clientHeight);
    scrollContainer.scrollTo({
      top: originalScrollTop + bounceDistance,
      behavior: 'smooth'
    });
    setTimeout(() => {
      scrollContainer.scrollTo({
        top: originalScrollTop,
        behavior: 'smooth'
      });
    }, 300);
  }, []);
  const handleImageLoad = useCallback((src, naturalWidth, naturalHeight) => {
    const aspectRatio = naturalHeight / naturalWidth;
    const isTall = aspectRatio > TALL_ASPECT_RATIO_THRESHOLD;
    setImageDimensions(prev => {
      const newDimensions = Object.assign({}, prev, {
        [src]: {
          width: naturalWidth,
          height: naturalHeight
        }
      });
      return newDimensions;
    });
    setLoadedImages(prev => new Set(prev).add(src));

    // Trigger bounce for tall images that haven't been bounced yet
    if (isTall && !tallImagesWithBounce.has(src)) {
      setTallImagesWithBounce(prev => new Set(prev).add(src));

      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        var _imageElementRef$curr;
        if ((_imageElementRef$curr = imageElementRef.current) !== null && _imageElementRef$curr !== void 0 && _imageElementRef$curr.parentElement) {
          triggerScrollBounce(imageElementRef.current.parentElement);
        }
      }, 200);
    }
  }, [TALL_ASPECT_RATIO_THRESHOLD, tallImagesWithBounce, triggerScrollBounce]);
  const isTallAspectRatio = useCallback(src => {
    const dimensions = imageDimensions[src];
    if (!dimensions) return false;
    const aspectRatio = dimensions.height / dimensions.width;
    return aspectRatio > TALL_ASPECT_RATIO_THRESHOLD;
  }, [imageDimensions]);
  useLayoutEffect(() => {
    if (stepIndex !== prevStepIndex) {
      if (imageElementRef.current && imageElementRef.current.parentElement) {
        imageElementRef.current.parentElement.scrollTop = 0;
      }
    }
  }, [fullscreen, stepIndex, prevStepIndex]);
  const renderImage = useCallback(item => {
    const isTall = isTallAspectRatio(item.source);
    const isLoaded = loadedImages.has(item.source);
    const image = /*#__PURE__*/_jsx(Image, {
      ref: imageElementRef,
      src: item.source,
      alt: item.alt,
      blockSize: isTall && isLoaded ? 'auto' : '100%',
      maxBlockSize: isTall && isLoaded ? 'none' : fullscreen ? '100%' : CAROUSEL_MEDIA_HEIGHT,
      inlineSize: "100%",
      maxInlineSize: "100%",
      objectFit: "contain",
      shape: "rounded",
      justifySelf: "center",
      onLoad: e => {
        const img = e.target;
        handleImageLoad(item.source, img.naturalWidth, img.naturalHeight);
      }
    });
    return fullscreen ? /*#__PURE__*/_jsx(ScrollingColumn, {
      maxHeight: "calc(100vh - 126px)",
      Overhang: NoopOverhang,
      children: image
    }) : /*#__PURE__*/_jsx(Clickable, {
      minInlineSize: "100%",
      onClick: () => {
        onItemClick === null || onItemClick === void 0 || onItemClick(item);
        onTrackInteraction === null || onTrackInteraction === void 0 || onTrackInteraction({
          action: 'click carousel screenshot'
        });
        onFullscreenChange === null || onFullscreenChange === void 0 || onFullscreenChange(!fullscreen);
        setFullscreen(!fullscreen);
      },
      children: /*#__PURE__*/_jsx(Flex, {
        boxShadow: fullscreen ? '400' : undefined,
        blockSize: CAROUSEL_MEDIA_HEIGHT,
        maxBlockSize: CAROUSEL_MEDIA_HEIGHT,
        alignItems: "center",
        children: /*#__PURE__*/_jsx(ScrollContainer, {
          maxHeight: CAROUSEL_MEDIA_HEIGHT,
          Overhang: NoopOverhang,
          children: image
        })
      })
    });
  }, [fullscreen, imageElementRef, onItemClick, onTrackInteraction, onFullscreenChange, isTallAspectRatio, handleImageLoad, loadedImages]);
  return /*#__PURE__*/_jsx(Fullscreen, {
    fullscreen: fullscreen,
    onClose: () => {
      // Wait a frame so that marketplace-embed knows we were previously
      // fullscreen. Otherwise, the entire modal/panel will close.
      requestAnimationFrame(() => {
        setFullscreen(false);
        onFullscreenChange === null || onFullscreenChange === void 0 || onFullscreenChange(false);
      });
    },
    children: /*#__PURE__*/_jsx(Carousel, {
      padding: "0",
      maxWidth: "100%",
      stepIndex: stepIndex,
      onStepIndexChange: e => setStepIndex(e.target.value),
      allowStepLoop: true
      // content
      ,
      steps: items.map((item, index) => ({
        alt: item.alt || `Item ${index + 1}`,
        content: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "center",
          gap: "100",
          children: [item.type === 'image' ? renderImage(item) : /*#__PURE__*/_jsx(VideoPlayer, {
            videoUrl: item.source,
            onTrackInteraction: onTrackInteraction,
            fullscreen: fullscreen,
            videoHeight: CAROUSEL_MEDIA_HEIGHT,
            thumbnailUrl: item.thumbnailUrl || undefined
          }), /*#__PURE__*/_jsxs(Text, {
            color: fullscreen ? 'fill-primary-disabled' : 'text-core-subtle',
            marginBlockStart: "100",
            typography: fullscreen ? undefined : 'detail-100',
            children: [`${index + 1}/${items.length}`, item.alt ? ` - ${item.alt}` : '']
          })]
        })
      }))
    })
  });
}